import logging from "utils/logging";
import {
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import Provider from "provider";
import {
  createCloseListenerAction,
  createOpenListenerAction,
modelsUpdated
} from "../../redux/actions";
import { playerEntity } from "../../models/player";
import { buildModelReducer } from "../../redux/reducers";
import { selectGameStatusObject } from "../gamestate/gameStateSlice";
import { AuthProvider } from "provider/auth";
import { PLAYER_STATES, PLAYER_PHASES, getInterRoundPhase } from './playerConstants';
const log = logging.getLogger("player-slice"); //eslint-disable-line no-unused-vars

const playerAdapter = createEntityAdapter();
const initialState = playerAdapter.getInitialState({
  userIsLoading: true,
  userId: null,
  playerIsLoading: true,
  // player: null,
  playerId: null,
  loadingPlayers: false,
});

const playerSlice = createSlice({
  name: "player",
  initialState,
  reducers: {
    userLoaded(state, action) {
      state.userIsLoading = false;
      state.userId = action.payload.userId;
    },
    loadingUser(state) {
      state.userId = null;
      state.userIsLoading = true;
    },
    loadingPlayer(state) {
      state.playerIsLoading = true;
      state.playerId = null;
    },
    playerLoaded(state, action) {
      log.debug("Player loaded: ", action);
      state.playerIsLoading = false;
      if (action.payload.player) {
        state.playerId = action.payload.player.id;
        playerAdapter.upsertOne(state, action.payload.player);
      }
    },
    creatingPlayer(state) {
      state.playerIsLoading = true;
      state.playerId = null;
    },
    watchingPlayers(state) {
      // Starting a watch for a (possibly different) game — clear the previous
      // game's roster first so stale players don't linger when the host
      // switches between games (the adapter is global, not keyed by game).
      playerAdapter.removeAll(state);
      state.loadingPlayers = true;
    },
  },
  extraReducers: (builder) => {
    buildModelReducer({
      builder,
      entity: playerEntity,
      loadingKey: "loadingPlayers",
      adapter: playerAdapter,
    });
  },
});

const getAnonymousUserAction = () => async (dispatch) => {
  dispatch(playerSlice.actions.loadingUser());

  const authProvider = AuthProvider();

  // let user = await Provider.getAnonymousUser();
  let user = await authProvider.getAnonymousUser();

  log.debug("Got anonymous user: ", user, user.uid);

  dispatch(playerSlice.actions.userLoaded({ userId: user.uid }));
};

const getPlayerForUserInGameAction = (userId, gameId) => async (dispatch) => {
  dispatch(playerSlice.actions.loadingPlayer());

  let player = await Provider.getPlayerForUserInGame(userId, gameId);

  log.debug("Got player: ", player);

  dispatch(playerSlice.actions.playerLoaded({ player }));
};

const createPlayerForUserInGameAction = (
  userId,
  gameId,
  playerName,
  signatureDataURL
) => async (dispatch) => {
  dispatch(playerSlice.actions.creatingPlayer());

  let player = await Provider.createPlayerForUserInGame(
    userId,
    gameId,
    playerName,
    signatureDataURL
  );

  dispatch(playerSlice.actions.playerLoaded({ player }));
};

/**
 * Updates a player's state for a specific game phase
 * 
 * @param {string} gameId - The game ID
 * @param {string} playerId - The player ID
 * @param {string} phase - The game phase (e.g., "INSTRUCTIONS")
 * @param {Object} stateData - The state data to update
 * @returns {Promise<Object>} - The updated player object
 */
const updatePlayerStateAction = (
  gameId,
  playerId,
  phase,
  stateData
) => async (dispatch) => {
  try {
    log.debug(`Updating player ${playerId} state for ${phase}:`, stateData);
    
    // Call provider to update player state
    const updatedPlayer = await Provider.updatePlayerState(
      gameId,
      playerId,
      phase,
      stateData
    );
    
    // Update the player in Redux store
    dispatch(playerSlice.actions.playerLoaded({ player: updatedPlayer }));
    
    return updatedPlayer;
  } catch (error) {
    log.error(`Error updating player ${playerId} state for ${phase}:`, error);
    return null;
  }
};

const watchPlayersActionBase = createOpenListenerAction(
  "player/watch_players",
  "watch-players"
);

const stopWatchingPlayersActionBase = createCloseListenerAction(
  "player/stop_watching_players",
  "watch-players"
);

const watchPlayersAction = (gameId) => (dispatch) => {
  dispatch(playerSlice.actions.watchingPlayers());
  dispatch(
    watchPlayersActionBase({ gameId }, async () => {
      log.info("Watching players for game: ", gameId);
      return await Provider.watchPlayersForGameId(gameId, (playersUpdate) => {
        log.info("Got updated players: ", playersUpdate);
        dispatch(modelsUpdated(playersUpdate, playerEntity));
      });
    })
  );
};

const stopWatchingPlayersAction = (gameId) => async (dispatch) => {
  dispatch(stopWatchingPlayersActionBase({ gameId }));
};

export { watchPlayersAction, stopWatchingPlayersAction };

/* ---- Selectors ---- */

export const {
  selectById: selectPlayerById,
  selectIds: selectPlayerIds,
  selectEntities: selectPlayerEntities,
  selectAll: selectAllPlayers,
  selectTotal: selectTotalPlayers,
} = playerAdapter.getSelectors((state) => state.player);

const selectPlayerState = (state) => state.player;
const selectUserId = createSelector(selectPlayerState, (s) => s.userId);
const selectLoadingUser = createSelector(
  selectPlayerState,
  (s) => s.userIsLoading
);
const selectLoadingPlayer = createSelector(
  selectPlayerState,
  (s) => s.playerIsLoading
);
const selectPlayerId = createSelector(selectPlayerState, (s) => s.playerId);
const selectPlayer = createSelector(
  (state) => state,
  selectPlayerId,
  (state, playerId) => {
    if (!playerId) {
      return null;
    }
    return selectPlayerById(state, playerId);
  }
);

const selectPlayerInControl = createSelector(
  (state) => state,
  selectGameStatusObject,
  (state, gameState) => {
    if (gameState.playerInControl) {
      return selectPlayerById(state, gameState.playerInControl);
    }

    return null;
  }
);

/**
 * Selects the readiness status of all players for the instructions phase
 * 
 * @returns {Object} - Object with player IDs as keys and readiness status as values
 */
const selectPlayersReadinessForInstructions = createSelector(
  selectAllPlayers,
  (players) => {
    const readinessMap = {};

    players.forEach(player => {
      const instructionsState = player.playerState?.[PLAYER_PHASES.INSTRUCTIONS];
      const isReady = instructionsState?.state === PLAYER_STATES.INSTRUCTIONS.READY;

      readinessMap[player.id] = {
        isReady,
        playerName: player.name,
        playerState: instructionsState
      };
    });

    return readinessMap;
  }
);

/**
 * Factory that creates a selector for inter-round readiness for a specific round.
 * Each round transition has its own phase key (e.g. INTER_ROUND_practice, INTER_ROUND_round1).
 */
const makeSelectPlayersReadinessForInterRound = (round) => createSelector(
  selectAllPlayers,
  (players) => {
    const phase = getInterRoundPhase(round);
    const readinessMap = {};

    players.forEach(player => {
      const phaseState = player.playerState?.[phase];
      const isReady = phaseState?.state === PLAYER_STATES.INTER_ROUND.READY;

      readinessMap[player.id] = {
        isReady,
        playerName: player.name,
      };
    });

    return readinessMap;
  }
);

export {
  selectPlayerState,
  selectLoadingUser,
  selectLoadingPlayer,
  selectUserId,
  selectPlayer,
  selectPlayerInControl,
  selectPlayersReadinessForInstructions,
  makeSelectPlayersReadinessForInterRound,
};

export {
  getAnonymousUserAction,
  getPlayerForUserInGameAction,
  createPlayerForUserInGameAction,
  updatePlayerStateAction,
};
export default playerSlice.reducer;
